import { useState, useEffect, useMemo, useCallback } from "react";
import { Badge, Empty, Popover, Segmented, Spin, message as antMessage } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import Icon from "@ant-design/icons";
import { NotificationIcon } from "../../assets/icons/icons";
import { useNavigate } from "react-router-dom";
import type { Notification, NotificationStatus } from "../../types/notifications";
import {
  mapApiNotificationToNotification,
  sortNotificationsByDate,
  formatNotificationTime,
} from "../../utils/notificationUtils";
import { api } from "../../services/api";
import NotificationItem from "./NotificationItem";
import Styles from "./NotificationPanel.module.scss";

// Hook to detect mobile screen size
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

interface NotificationPanelProps {
  onNotificationClick?: (notification: Notification) => void;
}

const NotificationPanel = ({ onNotificationClick }: NotificationPanelProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (filterType: "all" | "unread" = "all") => {
    try {
      setLoading(true);
      const response = await api.getNotifications(filterType);
      
      const mappedNotifications = response.notifications.map(mapApiNotificationToNotification);
      setNotifications(mappedNotifications);
      setUnreadCount(response.unread_count);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      antMessage.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch notifications when component mounts or tab changes
  useEffect(() => {
    fetchNotifications(activeTab);
  }, [activeTab, fetchNotifications]);

  // Refresh notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications(activeTab);
    }
  }, [isOpen, activeTab, fetchNotifications]);

  const filteredNotifications = useMemo(() => {
    // API already filters based on filter_type, so we just need to sort
    // Client-side filtering is kept as a safety measure in case API returns unexpected data
    let filtered = notifications;
    
    if (activeTab === "unread") {
      filtered = filtered.filter((n) => n.status === "unread");
    }
    
    return sortNotificationsByDate(filtered);
  }, [notifications, activeTab]);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      const numericId = parseInt(notificationId, 10);
      if (isNaN(numericId)) {
        console.error("Invalid notification ID:", notificationId);
        return;
      }

      await api.markNotifications({
        notification_ids: [numericId],
        is_read: true,
      });

      // Update local state optimistically
      const updated = notifications.map((n) =>
        n.id === notificationId ? { ...n, status: "read" as NotificationStatus } : n
      );
      setNotifications(updated);
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
      antMessage.error("Failed to mark notification as read");
      // Refresh notifications on error
      fetchNotifications(activeTab);
    }
  };

  const handleMarkAsUnread = async (notificationId: string) => {
    try {
      const numericId = parseInt(notificationId, 10);
      if (isNaN(numericId)) {
        console.error("Invalid notification ID:", notificationId);
        return;
      }

      await api.markNotifications({
        notification_ids: [numericId],
        is_read: false,
      });

      // Update local state optimistically
      const updated = notifications.map((n) =>
        n.id === notificationId ? { ...n, status: "unread" as NotificationStatus } : n
      );
      setNotifications(updated);
      setUnreadCount((prev) => prev + 1);
    } catch (error) {
      console.error("Error marking notification as unread:", error);
      antMessage.error("Failed to mark notification as unread");
      // Refresh notifications on error
      fetchNotifications(activeTab);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read when clicked
    if (notification.status === "unread") {
      handleMarkAsRead(notification.id);
    }

    // Navigate to the notification's path
    if (notification.navigationPath) {
      navigate(notification.navigationPath);
      setIsOpen(false);
    }

    // Call custom handler if provided
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const popoverContent = (
    <div className={Styles.notificationPanel}>
      <div className={Styles.panelHeader}>
        <div className={Styles.headerTitle}>Notifications</div>
        <div className={Styles.headerActions}>
          <Segmented
            size="small"
            options={[
              { label: "All", value: "all" },
              { label: "Unread", value: "unread" },
            ]}
            value={activeTab}
            onChange={(value) => setActiveTab(value as "all" | "unread")}
            className={Styles.tabs}
          />
          <CloseOutlined
            className={Styles.closeButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
        </div>
      </div>
      <div className={Styles.notificationList}>
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <Spin size="large" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Empty
            description={
              activeTab === "unread"
                ? "No unread notifications"
                : "No notifications"
            }
            style={{ padding: "20px" }}
          />
        ) : (
          filteredNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={() => handleMarkAsRead(notification.id)}
              onMarkAsUnread={() => handleMarkAsUnread(notification.id)}
              onClick={() => handleNotificationClick(notification)}
              formatTime={formatNotificationTime}
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      {isMobile && isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.4)",
            zIndex: 999,
            backdropFilter: "blur(2px)",
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
      <Popover
        content={popoverContent}
        trigger="click"
        placement={isMobile ? "bottom" : "bottomRight"}
        open={isOpen}
        onOpenChange={setIsOpen}
        overlayClassName={Styles.popoverOverlay}
        overlayInnerStyle={{ padding: 0 }}
        getPopupContainer={() => document.body}
        align={isMobile ? { offset: [0, -8] } : { offset: [0, 8] }}
        destroyOnHidden   
        autoAdjustOverflow
      >
      <div className={Styles.notificationIconWrapper}>
        <Badge count={unreadCount} offset={[-5, 5]} size="small">
          <div className={Styles.iconContainer}>
            <Icon component={NotificationIcon} className={Styles.notificationIcon} />
          </div>
        </Badge>
      </div>
    </Popover>
    </>
  );
};

export default NotificationPanel;

